import { strFromU8, unzipSync } from 'fflate';
import { importFiles, type ImportTarget } from './archive';
import { parseCsv } from './csv';
import type { PageParent } from './types';

export interface NotionImportOptions {
  /** Uploads an attachment (image, PDF, …) and returns its URL. Without it, attachments are left out. */
  upload?: (name: string, data: Uint8Array) => Promise<string>;
}

export interface NotionImportResult {
  roots: string[];
  pages: number;
  databases: number;
  files: number;
}

const TEXT = /\.(md|markdown|csv|html?)$/i;

/** Every file in the export; Notion splits big exports into zips inside the zip (`Export-…-Part-1.zip`). */
function unpack(zip: Uint8Array, prefix = '', out = new Map<string, Uint8Array>()): Map<string, Uint8Array> {
  for (const [path, bytes] of Object.entries(unzipSync(zip))) {
    if (path.endsWith('/') || path.startsWith('__MACOSX/') || path.endsWith('.DS_Store')) continue;
    if (/\.zip$/i.test(path)) unpack(bytes, prefix, out);
    else out.set(prefix + path, bytes);
  }
  return out;
}

const dirOf = (p: string) => (p.includes('/') ? p.slice(0, p.lastIndexOf('/') + 1) : '');

function resolvePath(from: string, href: string): string {
  let rel = href.split('#')[0]!.split('?')[0]!;
  try {
    rel = decodeURIComponent(rel);
  } catch {
    /* keep as is */
  }
  const norm: string[] = [];
  for (const part of (dirOf(from) + rel).split('/')) {
    if (part === '..') norm.pop();
    else if (part !== '.' && part !== '') norm.push(part);
  }
  return norm.join('/');
}

const MIME: Record<string, string> = { png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif', webp: 'image/webp', svg: 'image/svg+xml', pdf: 'application/pdf', mp4: 'video/mp4', mp3: 'audio/mpeg' };
export const mimeOf = (name: string) => MIME[name.split('.').pop()!.toLowerCase()] ?? 'application/octet-stream';

/**
 * Imports a Notion export (Settings → Export → Markdown & CSV, with subpages): pages, subpages, databases
 * with their row pages, links between pages, and attachments. Builds on `importFiles`, after fixing up what
 * Notion's export adds:
 * - zips inside the zip;
 * - each database twice (`DB.csv` and the complete `DB_all.csv`);
 * - row pages that repeat their properties as `Name: value` lines under the title;
 * - attachments next to the pages, linked by relative path (uploaded, links rewritten).
 */
export async function importNotionExport(target: ImportTarget, zip: Uint8Array, parent: PageParent, opts: NotionImportOptions = {}): Promise<NotionImportResult> {
  const all = unpack(zip);
  const text = new Map<string, Uint8Array>();
  const binary = new Map<string, Uint8Array>();
  for (const [path, bytes] of all) (TEXT.test(path) ? text : binary).set(path, bytes);

  const files = new Map<string, string>();
  for (const [path, bytes] of text) {
    // Prefer the complete `_all.csv`, under the regular name that the folder of row pages matches.
    const allMatch = /^(.*)_all\.csv$/i.exec(path);
    if (allMatch) files.set(`${allMatch[1]}.csv`, strFromU8(bytes));
    else if (!(/\.csv$/i.test(path) && text.has(path.replace(/\.csv$/i, '_all.csv')))) files.set(path, strFromU8(bytes));
  }

  // Row pages: drop the property lines Notion writes under the title (the values live in the database).
  for (const [csvPath, csv] of [...files].filter(([p]) => /\.csv$/i.test(p))) {
    const headers = new Set((parseCsv(csv)[0] ?? []).map((h) => h.replace(/^\uFEFF/, '').trim()));
    const folder = csvPath.replace(/\.csv$/i, '/');
    for (const [p, md] of files) {
      if (!p.startsWith(folder) || p.slice(folder.length).includes('/') || !/\.md$/i.test(p)) continue;
      const lines = md.split('\n');
      let i = lines[0]?.startsWith('# ') ? 1 : 0;
      while (lines[i] === '') i++;
      const start = i;
      while (i < lines.length && lines[i] !== '' && headers.has(lines[i]!.split(':')[0]!.trim()) && lines[i]!.includes(':')) i++;
      if (i > start) files.set(p, [...lines.slice(0, start), ...lines.slice(i)].join('\n'));
    }
  }

  // Attachments: upload the ones the pages link to and point the links at the uploads.
  let uploaded = 0;
  if (opts.upload) {
    const urls = new Map<string, string>();
    for (const [p, md] of files) {
      if (!/\.(md|markdown|html?)$/i.test(p)) continue;
      let out = md;
      for (const m of [...md.matchAll(/(!?\[[^\]]*\]\()([^)\s]+)((?:\s+"[^"]*")?\))|((?:src|href)=")([^"]+)(")/g)]) {
        const href = m[2] ?? m[5]!;
        if (/^[a-z][a-z0-9+.-]*:/i.test(href) || TEXT.test(href.split('#')[0]!)) continue;
        const path = resolvePath(p, href);
        const data = binary.get(path);
        if (!data) continue;
        let url = urls.get(path);
        if (!url) {
          url = await opts.upload(path.slice(path.lastIndexOf('/') + 1), data);
          urls.set(path, url);
          uploaded++;
        }
        out = out.replace(m[0], m[2] !== undefined ? `${m[1]}${url}${m[3]}` : `${m[4]}${url}${m[6]}`);
      }
      files.set(p, out);
    }
  }

  const roots = importFiles(target, files, parent);
  const kinds = [...files.keys()];
  return { roots, pages: kinds.filter((p) => !/\.csv$/i.test(p)).length, databases: kinds.filter((p) => /\.csv$/i.test(p)).length, files: uploaded };
}
